
import React from "react";
import { ExternalLink, BookOpen, Shield, Mail, FileText, Film } from "lucide-react";
import { Link } from "react-router-dom";
import { VersionDisplay } from "@/components/ui/version-display";
import { PWARefreshButton } from "@/components/layout/pwa-refresh-button";
import { isStandalone } from "@/utils/device";
import logoImage from "@/assets/logo.png";
import gameonLogo from "@/assets/gameon-logo.png";
import trialsLogo from "@/assets/trials-logo.png";
import subManagerLogo from "@/assets/sub-manager-logo.png";

export function VoteFooter() {
  const isPWA = isStandalone();

  return (
    <div className="w-full max-w-sm flex flex-col items-center divide-y divide-border/40">
      {/* Brand block */}
      <div className="flex flex-col items-center gap-2 pb-5 w-full">
        <div className="flex items-center gap-2">
          <img src={logoImage} alt="" className="h-6 w-6" />
          <span className="font-semibold text-sm">Vote.GameOn</span>
        </div>
        <div className="flex items-center gap-2">
          <VersionDisplay showRefreshButton={false} />
          {isPWA && <PWARefreshButton />}
        </div>
        <div className="text-xs text-muted-foreground">
          &copy; {new Date().getFullYear()} Vote.GameOn. All rights reserved.
        </div>
      </div>

      {/* Docs + Apps block */}
      <div className="flex flex-col items-center gap-4 py-5 w-full">
        <Link
          to="/docs"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
        >
          <BookOpen className="h-4 w-4" />
          <span>Docs</span>
        </Link>

        <div className="w-full">
          <h3 className="text-center font-medium text-sm text-muted-foreground mb-3">
            Apps
          </h3>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 justify-items-center">
            <a
              href="https://club.gameon.futbol/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm hover:text-primary transition-colors"
            >
              <img src={gameonLogo} alt="" className="h-5 w-5" />
              <span>Club Central</span>
              <ExternalLink className="h-3 w-3" />
            </a>
            <a
              href="https://club.gameon.futbol/sub-manager"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm hover:text-primary transition-colors"
            >
              <img src={subManagerLogo} alt="" className="h-5 w-5" />
              <span>Sub Manager</span>
              <ExternalLink className="h-3 w-3" />
            </a>
            <a
              href="https://trial.gameon.futbol/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm hover:text-primary transition-colors"
            >
              <img src={trialsLogo} alt="" className="h-5 w-5" />
              <span>Trial Manager</span>
              <ExternalLink className="h-3 w-3" />
            </a>
            <a
              href="https://board.gameon.futbol/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm hover:text-primary transition-colors"
            >
              <img
                src="https://etrspsphgbkapczqxboy.supabase.co/storage/v1/object/public/vote-images//soccerboard-trans.png"
                alt=""
                className="h-5 w-5"
              />
              <span>SoccerBoard</span>
              <ExternalLink className="h-3 w-3" />
            </a>
            <a
              href="https://reel.gameon.futbol/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm hover:text-primary transition-colors"
            >
              <Film className="h-5 w-5 text-primary" />
              <span>Reel</span>
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      </div>

      {/* Legal block */}
      <div className="flex items-center gap-4 text-xs text-muted-foreground pt-5 pb-2">
        <a
          href="https://club.gameon.futbol/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 hover:text-primary transition-colors"
        >
          <Shield className="h-3 w-3" />
          <span>Privacy</span>
        </a>
        <span>·</span>
        <Link
          to="/terms"
          className="inline-flex items-center gap-1 hover:text-primary transition-colors"
        >
          <FileText className="h-3 w-3" />
          <span>Terms</span>
        </Link>
        <span>·</span>
        <a
          href="mailto:support@gameon.futbol"
          className="inline-flex items-center gap-1 hover:text-primary transition-colors"
        >
          <Mail className="h-3 w-3" />
          <span>Contact</span>
        </a>
      </div>

      {/* Extra scroll safety on game vote pages */}
      {typeof window !== "undefined" && window.location.pathname.includes("/game/") && (
        <div className="h-10 border-0" />
      )}
    </div>
  );
}
